(() => {
  if (window.heroBannerInitialized) return;
  window.heroBannerInitialized = true;
  const instances = new WeakMap();

  function init(root) {
    if (instances.has(root)) return;
    const container = root.querySelector(".hero__slides");
    const slides = [...root.querySelectorAll("[data-hero-slide]")];
    if (!container || !slides.length) return;
    const abort = new AbortController();
    const listen = (el, type, fn) =>
      el?.addEventListener(type, fn, { signal: abort.signal });
    const dots = [...root.querySelectorAll("[data-hero-dot]")];
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    const autoplay = root.dataset.autoplay === "true";
    let index = 0,
      timer,
      visible = true,
      paused = motion.matches || !!window.Shopify?.designMode;
    let activeTransition = null;
    let destroyed = false;

    const canAnimate = () =>
      !!window.gsap &&
      !motion.matches &&
      document.documentElement.dataset.animations !== "false";
    function updateVideos() {
      slides.forEach((slide, i) =>
        slide.querySelectorAll("video").forEach((video) => {
          if (i === index && visible && !document.hidden && !motion.matches) {
            video.muted = true;
            video.play().catch(() => {});
          } else video.pause();
        }),
      );
    }
    function schedule() {
      clearTimeout(timer);
      updateVideos();
      if (
        destroyed ||
        activeTransition ||
        slides.length < 2 ||
        !autoplay ||
        paused ||
        !visible ||
        document.hidden ||
        root.contains(document.activeElement)
      )
        return;
      timer = setTimeout(
        () => show(index + 1),
        Math.max(5000, Number(root.dataset.interval) || 5000),
      );
    }
    function setActive(next) {
      index = next;
      slides.forEach((slide, i) => {
        slide.hidden = i !== index;
        slide.inert = i !== index;
      });
      dots.forEach((dot, i) =>
        dot.setAttribute("aria-current", String(i === index)),
      );
      updateVideos();
    }
    function finishTransition() {
      if (!activeTransition) return;
      const transition = activeTransition;
      activeTransition = null;
      transition.timeline?.kill();
      transition.overlay.remove();
      setActive(transition.next);
      schedule();
    }
    function makeWave() {
      const overlay = document.createElement("div");
      overlay.className = "hero__swave";
      overlay.setAttribute("aria-hidden", "true");
      const logos = document.createElement("div");
      logos.className = "hero__swave-logos";
      const url = root.dataset.waveLogo;
      if (root.dataset.waveShowLogos === "true") {
        const count = Math.min(
          18,
          Math.max(6, Number(root.dataset.waveLogoCount) || 12),
        );
        const positions = [
          [8, 19, 0.8, -15],
          [27, 13, 0.66, 13],
          [48, 27, 1.15, -8],
          [78, 14, 0.85, 12],
          [94, 32, 0.72, -12],
          [16, 52, 1.25, 9],
          [36, 64, 0.82, -13],
          [65, 49, 1.3, 16],
          [87, 67, 0.95, -9],
          [7, 84, 0.68, 8],
          [29, 88, 1.08, -12],
          [53, 82, 0.76, 15],
          [74, 91, 1.1, -8],
          [96, 88, 0.72, 12],
          [42, 42, 0.65, 10],
          [58, 12, 0.76, -9],
          [19, 33, 0.58, 8],
          [85, 43, 0.63, -10],
        ];
        for (let i = 0; i < count; i++) {
          const [x, y, size, rotation] = positions[i];
          const item = document.createElement("div");
          item.className = "hero__swave-logo";
          item.style.setProperty("--x", `${x}%`);
          item.style.setProperty("--y", `${y}%`);
          item.style.setProperty("--logo-size", String(size));
          item.dataset.rotation = rotation;
          if (url) {
            const img = document.createElement("img");
            img.src = url;
            img.alt = "";
            item.appendChild(img);
          } else {
            const fallback = document.createElement("span");
            fallback.className = "hero__swave-fallback";
            fallback.textContent = "HV";
            item.appendChild(fallback);
          }
          logos.appendChild(item);
        }
      }
      overlay.appendChild(logos);
      container.appendChild(overlay);
      return { overlay, items: [...logos.children] };
    }
    // Smooth S-shaped vertical boundary. Phase 1 covers left-to-right;
    // phase 2 uncovers left-to-right, revealing the new slide.
    function waveShape(progress, reveal = false) {
      const edge = -35 + progress * 170;
      const points = reveal ? [] : ["0% 0%"];
      for (let step = 0; step <= 48; step++) {
        const y = step / 48;
        const x = edge + Math.sin(y * Math.PI * 2) * 10;
        points.push(`${x}% ${y * 100}%`);
      }
      if (reveal) points.push("100% 100%", "100% 0%");
      else points.push("0% 100%");
      return `polygon(${points.join(",")})`;
    }
    function show(next) {
      if (destroyed) return;
      if (activeTransition) finishTransition();
      const target = (next + slides.length) % slides.length;
      if (target === index) {
        schedule();
        return;
      }
      clearTimeout(timer);
      if (!canAnimate()) {
        setActive(target);
        schedule();
        return;
      }

      const { overlay, items } = makeWave();
      const duration = Math.min(
        3,
        Math.max(1, Number(root.dataset.waveDuration) || 1.5),
      );
      const state = { progress: 0 };
      const tl = window.gsap.timeline();
      activeTransition = { overlay, timeline: tl, next: target };
      const gsap = window.gsap;
      overlay.style.clipPath = waveShape(0);
      tl.to(state, {
        progress: 1,
        duration: duration * 0.48,
        ease: "power2.inOut",
        onUpdate: () => {
          overlay.style.clipPath = waveShape(state.progress);
        },
      });
      tl.call(() => {
        if (activeTransition?.overlay === overlay) setActive(target);
      });
      tl.set(state, { progress: 0 });
      tl.to(state, {
        progress: 1,
        duration: duration * 0.52,
        ease: "power2.inOut",
        onUpdate: () => {
          overlay.style.clipPath = waveShape(state.progress, true);
        },
      });
      if (items.length) {
        // Los logos se revelan con la ola y se desvanecen antes de terminar.
        gsap.set(items, { xPercent: -50, yPercent: -50, opacity: 0 });
        const appear = duration * 0.24;
        tl.fromTo(
          items,
          {
            opacity: 0,
            scale: 0.55,
            rotation: (i) => Number(items[i].dataset.rotation) - 20,
            y: 20,
          },
          {
            opacity: 0.95,
            scale: 1,
            rotation: (i) => Number(items[i].dataset.rotation),
            y: 0,
            stagger: { each: duration * 0.022, from: "random" },
            duration: appear,
            ease: "back.out(1.35)",
          },
          duration * 0.16,
        );
        tl.to(
          items,
          {
            y: (i) => (i % 2 ? -16 : 14),
            rotation: "+=8",
            duration: duration * 0.4,
            ease: "sine.inOut",
          },
          duration * 0.39,
        );
        tl.to(
          items,
          {
            opacity: 0,
            scale: 0.86,
            duration: duration * 0.17,
            stagger: { each: duration * 0.008, from: "random" },
          },
          duration * 0.79,
        );
      }
      tl.call(
        () => {
          if (activeTransition?.overlay === overlay) finishTransition();
        },
        null,
        duration,
      );
    }

    listen(root.querySelector("[data-hero-prev]"), "click", () =>
      show(index - 1),
    );
    listen(root.querySelector("[data-hero-next]"), "click", () =>
      show(index + 1),
    );
    dots.forEach((dot, i) => listen(dot, "click", () => show(i)));
    listen(root, "focusin", schedule);
    listen(root, "focusout", () => setTimeout(schedule, 0));
    listen(document, "visibilitychange", schedule);
    const ageGate = document.querySelector("[data-age-gate]");
    if (ageGate) listen(ageGate, "close", schedule);
    listen(motion, "change", () => {
      if (motion.matches) {
        paused = true;
        finishTransition();
      } else paused = !!window.Shopify?.designMode;
      schedule();
    });
    listen(root, "shopify:block:select", (event) => {
      const selected = slides.findIndex(
        (slide) => slide.dataset.blockId === event.detail.blockId,
      );
      if (selected >= 0) {
        paused = true;
        show(selected);
      }
    });
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      schedule();
    });
    observer.observe(root);
    const controls = root.querySelector("[data-hero-controls]");
    if (controls) controls.hidden = false;
    setActive(0);
    schedule();
    instances.set(root, () => {
      destroyed = true;
      paused = true;
      clearTimeout(timer);
      activeTransition?.timeline?.kill();
      activeTransition?.overlay.remove();
      activeTransition = null;
      slides.forEach((slide) =>
        slide.querySelectorAll("video").forEach((video) => video.pause()),
      );
      observer.disconnect();
      abort.abort();
      instances.delete(root);
    });
  }
  const scan = (scope) => {
    if (scope.matches?.("[data-hero-slider]")) init(scope);
    scope.querySelectorAll("[data-hero-slider]").forEach(init);
  };
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => scan(document), {
      once: true,
    });
  else scan(document);
  window.addEventListener("load", () => scan(document), { once: true });
  document.addEventListener("shopify:section:load", (event) =>
    scan(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    if (event.target.matches?.("[data-hero-slider]"))
      instances.get(event.target)?.();
    event.target
      .querySelectorAll("[data-hero-slider]")
      .forEach((root) => instances.get(root)?.());
  });
})();
